import type { Ancestor } from "@/entities/ancestor";
import type { AssetCreatePayload } from "@/entities/asset";
import type { Coordinate } from "@/types";

import { Skeleton } from "antd";
import { useTranslation } from "react-i18next";

import { useCreate } from "@/hooks/queries";
import { useNavigateToRessource } from "@/hooks/useNavigateToRessource";
import { getAssetConditionRatingFieldValue } from "@/modules/asset/asset-condition-rating-field";
import { getAssetRiskFieldValue } from "@/modules/asset/asset-risk-field";
import { getAssetSelectOptionValue } from "@/modules/assets/assets-select-parent";
import { getLocationSelectOptionValue } from "@/modules/location/location-select-parent";
import { useOrganizationSelectOptionValue } from "@/modules/organization/organization-select";

import AssetForm from "./AssetForm";

const invalidateKeys = [
  ["locations", "coordinates"],
  ["assets", "coordinates"],
  ["tree", "locations"],
];

const Create = ({
  ancestors = [],
  defaultCoordinate,
}: {
  ancestors?: Ancestor[];
  defaultCoordinate?: Coordinate;
}) => {
  const { t } = useTranslation();

  const mutation = useCreate("assets", { invalidateKeys });
  const saveButtonLabel = t("global:add");

  const onNavigateToAsset = useNavigateToRessource<AssetCreatePayload>(
    "/assets/{{id}}",
    ({ data }) => data.data.id
  );

  const { organizationSelectValue, isLoading } =
    useOrganizationSelectOptionValue(ancestors);

  const initialValues = {
    organization: organizationSelectValue,
    location: getLocationSelectOptionValue(ancestors),
    asset: getAssetSelectOptionValue(ancestors),
    coordinate: defaultCoordinate,
    conditionRating: getAssetConditionRatingFieldValue(),
    risk: getAssetRiskFieldValue(),
  };

  if (isLoading) {
    return <Skeleton active />;
  }

  return (
    <AssetForm
      mutation={mutation}
      saveButtonLabel={saveButtonLabel}
      initialValues={initialValues}
      onMutate={onNavigateToAsset}
    />
  );
};

export default Create;
